/* eslint-disable @angular-eslint/prefer-inject */
import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import { WarehouseItemSettingsService } from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { WarehouseItemSettingStore } from './warehouse-item-setting.store';
import {
    WarehouseItemSettingCreate,
    WarehouseItemSettingRead,
    WarehouseItemSettingSearchParams,
} from '../../entities/warehouse-item-setting.dto';
import { Operation } from 'fast-json-patch';

@Injectable()
export class WarehouseItemSettingDataService extends AbstractDataService<
    WarehouseItemSettingRead,
    WarehouseItemSettingCreate
> {
    protected EntityName = PFWmsEntities.WarehouseItemSetting;

    constructor(
        private readonly service: WarehouseItemSettingsService,
        store: WarehouseItemSettingStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }

    updatePut$(
        id: string,
        body: WarehouseItemSettingCreate
    ): Observable<WarehouseItemSettingRead> {
        return this.service
            .warehouseItemSettingsUpdatePut({
                id,
                warehouseItemSettingsViewModelCreateBody: body,
            })
            .pipe(
                map(entity =>
                    this.withDefaultValues(entity as WarehouseItemSettingRead)
                ),
                tap(entity => this.store.update(entity))
            );
    }

    protected createObs$(
        body: WarehouseItemSettingCreate
    ): Observable<WarehouseItemSettingRead> {
        return this.service.warehouseItemSettingsCreate({
            warehouseItemSettingsViewModelCreateBody: body,
        }) as Observable<WarehouseItemSettingRead>;
    }

    protected deleteObs$(id: string): Observable<WarehouseItemSettingRead> {
        return this.service.warehouseItemSettingsDelete({
            id,
        }) as Observable<WarehouseItemSettingRead>;
    }

    protected undoDeleteObs$(id: string): Observable<WarehouseItemSettingRead> {
        return this.service.warehouseItemSettingsUndoDelete({
            id,
        }) as Observable<WarehouseItemSettingRead>;
    }

    protected getObs$(id: string): Observable<WarehouseItemSettingRead> {
        return this.service.warehouseItemSettingsReadById({
            id,
        }) as Observable<WarehouseItemSettingRead>;
    }

    protected updateObs$(
        id: string,
        operations: Operation[],
        _modified: WarehouseItemSettingCreate
    ): Observable<WarehouseItemSettingRead> {
        return this.service.warehouseItemSettingsUpdatePatch({
            id,
            operation: operations,
        }) as Observable<WarehouseItemSettingRead>;
    }

    protected searchObs$(
        searchParams: WarehouseItemSettingSearchParams
    ): Observable<IPagedResult<WarehouseItemSettingRead>> {
        return this.service.warehouseItemSettingsSearch(
            searchParams
        ) as Observable<IPagedResult<WarehouseItemSettingRead>>;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<
        IPagedResult<EntityChangeTrackingDto<WarehouseItemSettingRead>>
    > {
        return this.service.warehouseItemSettingsGetChanges(
            searchParams
        ) as Observable<
            IPagedResult<EntityChangeTrackingDto<WarehouseItemSettingRead>>
        >;
    }
}
